// client/src/components/pages/LexiStar/PlanDispatch/NextLoadPanel.tsx
//
// *Next load from here* (LS-09 §5.24, FR-TRIP-021): sits at the end of a truck's run strip.
// Source pre-filled with the truck's current or next offloading site (the run summary's
// `next_source`, else the last trip's destination); pick destination, material and order; the
// panel shows the route (approved, or *proposed* — offered as a golden run afterwards), distance
// and time. "Add to run" → POST /runs/:id:appendTrip, which returns the trip with its litre
// allowance and its driver; then *Dispatch* → POST /trips/:id:dispatch.
//
// ST-00198: the appended trip keeps the run's driver through driver_id (the server's rule: the
// run's driver, else the driver its last trip was given by choice, else the truck's allocation
// at the start) — shown with the allowance before dispatching. Converted to TypeScript.

import { useMemo, useState } from 'react';
import { FaPlus, FaPaperPlane } from 'react-icons/fa';
import { ThemedSelect, useAuth } from '../../../../types/commonComponents';
import type { TripDriverFields } from '../../../../types/lexistar-trip-drivers';
import { appendTripToRun, dispatchTrip } from '../../../../services/lexistarStatusBoard';
import { fmtKm, fmtL, fmtDuration } from '../StatusBoard/format';
import usePlanReference from './usePlanReference';
import type { Num } from './planTypes';

const input =
  'w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

/** The run summary the panel continues (GET /runs/:id/summary). */
export interface NextLoadSummary {
  run: { id: string };
  trips?: Array<{ seq: number; to_location_id?: number | string | null; material?: string | null }>;
  next_source?: { location_id: number | string; name?: string | null } | null;
}

/** The appended trip (POST /runs/:id:appendTrip → trip). */
interface AppendedTrip extends Partial<TripDriverFields> {
  id: string;
  reference?: string | null;
  planned_km?: Num;
  budget_original_l?: Num;
}

const DRIVER_SOURCE_NOTE: Record<string, string> = {
  allocation: 'allocated',
  chosen: "the run's driver",
  tag: 'not in the register',
};

export default function NextLoadPanel({
  summary,
  onChanged,
}: {
  summary: NextLoadSummary;
  onChanged?: (message: string) => void;
}) {
  const { hasPermission } = useAuth();
  const canDispatch = hasPermission('lexistar.trips.dispatch');
  const ref = usePlanReference();
  const lastTrip = [...(summary?.trips || [])].sort((a, b) => b.seq - a.seq)[0];
  const defaultSource = summary?.next_source?.location_id ?? lastTrip?.to_location_id ?? '';

  const [from, setFrom] = useState(defaultSource === '' ? '' : String(defaultSource));
  const [to, setTo] = useState('');
  const [material, setMaterial] = useState(lastTrip?.material || '');
  const [orderRef, setOrderRef] = useState('');
  const [tonnes, setTonnes] = useState('');
  const [created, setCreated] = useState<AppendedTrip | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lane = useMemo(
    () =>
      ref.lanes.find(
        (l) =>
          String(l.from_location_id) === String(from) && String(l.to_location_id) === String(to)
      ) || null,
    [ref.lanes, from, to]
  );
  const route = useMemo(() => {
    if (!lane) return null;
    const onLane = ref.routes.filter((r) => r.lane_id === lane.id);
    return (
      onLane.find((r) => r.is_primary) ||
      onLane.find((r) => r.status === 'approved') ||
      onLane[0] ||
      null
    );
  }, [lane, ref.routes]);

  // Destinations served from this source first (known lanes), then every site.
  const destinations = useMemo(() => {
    const laneTo = ref.lanes
      .filter((l) => String(l.from_location_id) === String(from))
      .map((l) => String(l.to_location_id));
    const all = ref.sites.map((s) => ({ id: String(s.location_id), name: s.location_name }));
    const known = all.filter((s) => laneTo.includes(s.id));
    const others = all.filter((s) => !laneTo.includes(s.id) && s.id !== String(from));
    return { known, others };
  }, [ref.lanes, ref.sites, from]);

  const errorText = (err: unknown, fallback: string): string => {
    const e = err as { response?: { data?: { error?: string } }; message?: string } | null;
    return e?.response?.data?.error || e?.message || fallback;
  };

  const add = async () => {
    if (!from || !to) {
      setError('Choose where the truck loads and where it delivers');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // dispatch:false — the API dispatches on append by default; the allowance is
      // shown first and the dispatcher sends it (LS-09 §5.24).
      const res = await appendTripToRun(summary.run.id, {
        dispatch: false,
        from_location_id: Number(from),
        to_location_id: Number(to),
        lane_id: lane?.id,
        route_id: route?.id,
        load: {
          material: material || null,
          order_ref: orderRef || null,
          planned_tonnes: tonnes === '' ? null : Number(tonnes),
        },
      });
      setCreated((res.trip || res) as AppendedTrip);
    } catch (err) {
      setError(errorText(err, 'Could not add the trip'));
    } finally {
      setBusy(false);
    }
  };

  const dispatch = async () => {
    if (!created) return;
    setBusy(true);
    setError(null);
    try {
      await dispatchTrip(created.id);
      const ref2 = created.reference;
      setCreated(null);
      setTo('');
      setOrderRef('');
      setTonnes('');
      onChanged?.(`Dispatched ${ref2} as the next load.`);
    } catch (err) {
      setError(errorText(err, 'Dispatch failed'));
    } finally {
      setBusy(false);
    }
  };

  const driverLabel = created ? created.driver_name || created.driver_tag || 'No driver' : '';
  const driverNote = created?.driver_source ? DRIVER_SOURCE_NOTE[created.driver_source] : null;

  return (
    <li
      className="flex-shrink-0 w-72 p-3 bg-surface border-2 border-dashed border-primary-300 rounded-lg text-xs text-slate-700 space-y-2"
      aria-label="Next load from here"
    >
      <p className="text-sm font-semibold text-slate-900">Next load from here</p>
      {error && <p className="text-red-700">{error}</p>}
      {created ? (
        <>
          <p>
            <span className="font-mono font-semibold">{created.reference}</span> added to the run.
          </p>
          <dl className="grid grid-cols-2 gap-x-2 gap-y-1">
            <dt className="text-slate-500">Distance</dt>
            <dd className="tabular-nums">{fmtKm(created.planned_km)}</dd>
            <dt className="text-slate-500">Allowance</dt>
            <dd className="tabular-nums font-semibold">{fmtL(created.budget_original_l)}</dd>
            <dt className="text-slate-500">Driver</dt>
            <dd>
              {driverLabel}
              {driverNote && <span className="block text-slate-500">{driverNote}</span>}
            </dd>
          </dl>
          {canDispatch ? (
            <button
              type="button"
              onClick={dispatch}
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-lg disabled:bg-slate-400"
            >
              <FaPaperPlane size={11} /> Dispatch
            </button>
          ) : (
            <p className="text-slate-500">Dispatching needs lexistar.trips.dispatch.</p>
          )}
        </>
      ) : (
        <>
          <ThemedSelect
            value={from}
            onChange={(e) => setFrom(String(e.target.value))}
            className="w-full h-9 text-sm"
            aria-label="Loads at"
          >
            <option value="">Loads at...</option>
            {ref.sites.map((s) => (
              <option key={s.location_id} value={String(s.location_id)}>
                {s.location_name}
              </option>
            ))}
          </ThemedSelect>
          <ThemedSelect
            value={to}
            onChange={(e) => setTo(String(e.target.value))}
            className="w-full h-9 text-sm"
            aria-label="Delivers to"
          >
            <option value="">Delivers to...</option>
            {destinations.known.length > 0 && (
              <optgroup label="Lanes from here">
                {destinations.known.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </optgroup>
            )}
            <optgroup label="Other sites">
              {destinations.others.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </optgroup>
          </ThemedSelect>
          <div className="grid grid-cols-[1fr_1fr_56px] gap-1">
            <input
              value={material}
              onChange={(e) => setMaterial(e.target.value)}
              placeholder="Material"
              aria-label="Next load material"
              className={input}
            />
            <input
              value={orderRef}
              onChange={(e) => setOrderRef(e.target.value)}
              placeholder="Order"
              aria-label="Next load order"
              className={input}
            />
            <input
              type="number"
              min="0"
              value={tonnes}
              onChange={(e) => setTonnes(e.target.value)}
              placeholder="t"
              aria-label="Next load tonnes"
              className={input}
            />
          </div>
          {to && (
            <p className="text-slate-600" role="status">
              {route ? (
                <>
                  Route <span className="font-mono">{route.code}</span>
                  {route.status === 'proposed'
                    ? ' — proposed; offered as a golden run afterwards'
                    : ' — approved'}
                  {route.planned_km != null && ` · ${fmtKm(route.planned_km)}`}
                  {route.planned_min != null && ` · ${fmtDuration(route.planned_min * 60)}`}
                </>
              ) : (
                'No route on this lane yet — the engine proposes one (unapproved route).'
              )}
            </p>
          )}
          <button
            type="button"
            onClick={add}
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold text-primary-700 bg-surface border border-primary-400 hover:bg-primary-50 rounded-lg disabled:opacity-60"
          >
            <FaPlus size={11} /> {busy ? 'Adding...' : 'Add to run'}
          </button>
        </>
      )}
    </li>
  );
}
